import _slicedToArray from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import { makeReference } from '@apollo/client';
import { AppAlertsContext } from '@infl-ts/ui-app-alerts';
import { useFocusErrors } from '@infl-ts/util-react-hook-form';
import { useMutationMapper } from '@infl-ts/util-use-mutation-mapper';
import { Box, FormControl } from '@mui/material';
import * as A from 'fp-ts/Array';
import * as E from 'fp-ts/Either';
import { constant, constVoid, flow, pipe } from 'fp-ts/function';
import * as R from 'fp-ts/Record';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AsyncButton, SaveStates } from '../../async-button';
import { TextFormField } from '../../form';
import { saveFailure } from './alerts';
import { BasicAccountSettingsBox } from './basic-account-settings-box';
import { useUpdateSettingsMutation } from './mutations';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var PureBasicAcountSettingsSection = function PureBasicAcountSettingsSection(_ref) {
  var accountSettingsForm = _ref.accountSettingsForm,
      handleSaveChanges = _ref.handleSaveChanges,
      mutationStatus = _ref.mutationStatus;
  var handleSubmit = accountSettingsForm.handleSubmit,
      control = accountSettingsForm.control,
      errors = accountSettingsForm.formState.errors;
  var Saving = SaveStates.Saving,
      Default = SaveStates.Default,
      Success = SaveStates.Success,
      Error = SaveStates.Error;
  var mutationValue = useMutationMapper(mutationStatus, function (_ref2) {
    var editProfile = _ref2.editProfile;
    return editProfile.__typename === 'EditProfileErrors' ? E.left(editProfile) : E.right(editProfile);
  }, [Saving, R.isEmpty(accountSettingsForm.formState.dirtyFields) ? Success : Default, Error, Default]);

  var _useFocusErrors = useFocusErrors(),
      questionRef = _useFocusErrors.questionRef,
      focusOnFirstError = _useFocusErrors.focusOnFirstError;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsxs(BasicAccountSettingsBox, {
    questionRef: questionRef,
    "data-element": "BasicAccountSettingsBox",
    "data-component": "PureBasicAcountSettingsSection",
    "data-source-file": "basic-settings.tsx",
    children: [/*#__PURE__*/_jsx(FormControl, {
      id: "email",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-component": "PureBasicAcountSettingsSection",
      "data-source-file": "basic-settings.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "email",
        label: t('Email'),
        required: true,
        error: errors.email,
        control: control,
        "data-element": "TextFormField",
        "data-component": "PureBasicAcountSettingsSection",
        "data-source-file": "basic-settings.tsx"
      })
    }), /*#__PURE__*/_jsx(FormControl, {
      id: "confirmPassword",
      sx: {
        width: 1,
        mb: 3
      },
      "data-element": "FormControl",
      "data-source-file": "basic-settings.tsx",
      children: /*#__PURE__*/_jsx(TextFormField, {
        name: "confirmPassword",
        label: t('Confirm password'),
        required: true,
        type: "password",
        autoComplete: "current-password",
        error: errors.confirmPassword,
        control: control,
        "data-element": "TextFormField",
        "data-source-file": "basic-settings.tsx"
      })
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        my: 1,
        width: 1,
        display: 'flex',
        justifyContent: 'flex-end'
      },
      "data-element": "Box",
      "data-source-file": "basic-settings.tsx",
      children: /*#__PURE__*/_jsx(AsyncButton, {
        status: mutationValue,
        onClick: handleSubmit(handleSaveChanges, focusOnFirstError),
        text: ['Saving...', 'Saved', 'Save changes', 'Save changes'],
        "data-element": "AsyncButton",
        "data-source-file": "basic-settings.tsx"
      })
    })]
  });
};

var useProps = function useProps(_ref3) {
  var profileId = _ref3.profileId,
      settings = _ref3.settings,
      name = _ref3.name;
  var accountSettingsForm = useForm({
    defaultValues: settings
  });

  var _useContext = useContext(AppAlertsContext),
      showAlert = _useContext.showAlert;

  var _useTranslation2 = useTranslation(),
      t = _useTranslation2.t;

  var setError = function setError(errors, _setError) {
    return pipe(errors, A.map(function (error) {
      if (error.__typename === 'InvalidEmailError' || error.__typename === 'SameEmailError') {
        _setError('email', error);
      } else if (error.__typename === 'EmailTakenError') {
        _setError('email', {
          message: 'Email is already taken by another user'
        });
      } else {
        _setError('confirmPassword', {
          message: 'Incorrect Password'
        });
      }
    }));
  };

  var _useUpdateSettingsMut = useUpdateSettingsMutation({
    onCompleted: E.match(function () {
      return showAlert(saveFailure(handleSaveChanges, t));
    }, function (_ref4) {
      var editProfile = _ref4.editProfile;
      if (editProfile.__typename === 'EditProfileErrors' && editProfile.errors[0].__typename === 'NotFoundError') showAlert(saveFailure(handleSaveChanges, t));
    }),
    update: function update(cache, data) {
      return pipe(data, E.match(constVoid, function (_ref5) {
        var editProfile = _ref5.editProfile;

        if (editProfile.__typename === 'EditProfileResult') {
          cache.modify({
            id: cache.identify(makeReference("Profile:".concat(profileId))),
            fields: {
              email: constant(editProfile.result.email)
            }
          });
          accountSettingsForm.reset(editProfile.result);
        } else {
          setError(editProfile.errors, accountSettingsForm.setError);
        }
      }));
    }
  }),
      _useUpdateSettingsMut2 = _slicedToArray(_useUpdateSettingsMut, 2),
      updateSettings = _useUpdateSettingsMut2[0],
      mutationStatus = _useUpdateSettingsMut2[1];

  var handleSaveChanges = function handleSaveChanges() {
    return updateSettings({
      variables: {
        input: {
          name: name,
          newEmail: {
            email: accountSettingsForm.getValues('email'),
            confirmPassword: accountSettingsForm.getValues('confirmPassword')
          }
        }
      }
    });
  };

  return {
    accountSettingsForm: accountSettingsForm,
    handleSaveChanges: handleSaveChanges,
    mutationStatus: mutationStatus
  };
};

export default Object.assign(flow(useProps, PureBasicAcountSettingsSection), {
  displayName: 'BasicAcountSettingsSection'
});